/** The ranger on foot: stance, speed, encumbrance, fatigue, footstep noise (02 §4-§5). */

import { makeNoise } from '../alert';
import type { Ctx } from '../ctx';
import { angleDiff, atan2, PI, wrapAngle } from '../math';
import { iceAt, inTrench, resolveMovement, waterAt } from '../statics';
import { DRAG, GEAR_WEIGHT, HAZARDS, NOISE, RANGER, WEIGHT } from '../tuning';
import { STEALTH } from '../tuningStealth';
import { DT, type Gear, type Inventory, type Ranger, type SimMods } from '../types';
import { stormFatigue } from '../weather';
import { tractionDirection } from './traction';

/**
 * Radius of one of his footsteps on dry ground, metres (02 §4), before water and snow have their say.
 * ROUND7: a crouched walk is `STEALTH.sneakNoise`. The HUD's noise ring should draw this same figure.
 */
export function footstepGait(
  r: Pick<Ranger, 'stance' | 'running' | 'sneaking'>,
  mods: Pick<SimMods, 'walkNoise'>,
): number {
  if (r.stance === 'crawl') return NOISE.crawl;
  if (r.running) return NOISE.run;
  return r.sneaking === true ? STEALTH.sneakNoise : NOISE.walk * mods.walkNoise;
}

/** Units on his back. Rare gear counts when given: a smoke grenade each, the pistol while it has rounds (04 §6). */
export function carriedWeight(inv: Inventory, gear?: Gear): number {
  const kit = inv.magazines * WEIGHT.magazine + inv.grenades * WEIGHT.grenade + inv.laws * WEIGHT.law
    + inv.bombs * WEIGHT.bomb + inv.firstAid * WEIGHT.firstAid;
  if (!gear) return kit;
  return kit + gear.smoke * GEAR_WEIGHT.smoke + (gear.pistolRounds > 0 ? GEAR_WEIGHT.pistol : 0)
    + (gear.nvg ? GEAR_WEIGHT.nvg : 0) + (gear.wireCutters ? GEAR_WEIGHT.wireCutters : 0)
    + (gear.ghillie === true ? GEAR_WEIGHT.ghillie : 0);
}

export function stepMovement(ctx: Ctx): void {
  const { world: w, cmd, statics } = ctx;
  const r = w.ranger;
  const downed = r.status === 'downed';

  r.aim = cmd.aim;
  // R4: run down by a lorry, he is flat on the ground until he has picked himself up (specials/vehicle.ts).
  if (r.flatTicks !== undefined && r.flatTicks > 0) r.flatTicks--;
  r.stance = downed || cmd.crawl || (r.flatTicks ?? 0) > 0 ? 'crawl' : 'stand';
  r.inTrench = inTrench(statics.def, r.x, r.z);

  // HAZ: tangled in barbed wire he goes nowhere until he has torn free (02 §9.2; hazards/wire.ts).
  const tangled = w.hazards.wireTicks > 0;
  let mx = tangled ? 0 : cmd.moveX;
  let mz = tangled ? 0 : cmd.moveZ;
  const len = Math.sqrt(mx * mx + mz * mz);
  if (len > 1) {
    mx /= len;
    mz /= len;
  }
  const wantsMove = len > 0.05;
  // OBJ: a quest item on his back (the radio set, 10 §3) is felt like anything else he carries.
  const over = Math.max(0, carriedWeight(r.inventory, r.gear) + (r.questWeight ?? 0) - RANGER.freeWeight);
  const dragging = r.dragging >= 0;

  // The body faces where the feet are going; the chest turns toward the aim. Moving well
  // away from the aim is a backpedal: he keeps facing the threat and walks backwards.
  const moveAngle = wantsMove ? atan2(mx, mz) : r.facing;
  const offAim = wantsMove ? Math.abs(angleDiff(moveAngle, cmd.aim)) : 0;
  const DEG = PI / 180;
  let faceTo = cmd.aim;
  r.backpedal = false;
  if (wantsMove) {
    if (r.stance === 'crawl' || offAim <= RANGER.backpedalBeyondDeg * DEG) faceTo = moveAngle;
    else {
      faceTo = moveAngle + PI;
      r.backpedal = true;
    }
  }
  const turn = (r.stance === 'crawl' ? RANGER.proneTurnRate : RANGER.turnRate) * DT;
  r.facing = wrapAngle(r.facing + Math.max(-turn, Math.min(turn, angleDiff(r.facing, faceTo))));

  if (r.winded && r.fatigue < 0.5) r.winded = false;
  r.running = wantsMove && cmd.run && r.stance === 'stand' && !r.winded && r.staggerTicks === 0 && !dragging
    && offAim <= RANGER.runArcDeg * DEG;
  // ROUND7: the crouched walk. Crawling and running both win over it; down, knocked flat or hauling a
  // body he cannot. The flag is written only once he has sneaked, so a world where he never has
  // serialises - and hashes - exactly as it always did (the recorded replays depend on it).
  const sneaking = cmd.sneak && r.stance === 'stand' && !r.running && !dragging;
  if (sneaking || r.sneaking !== undefined) r.sneaking = sneaking;

  const mods = w.mods;
  let speed: number = r.stance === 'crawl' ? RANGER.crawlSpeed * mods.crawlSpeed
    : r.running ? RANGER.runSpeed * mods.runSpeed : sneaking ? STEALTH.sneakSpeed : RANGER.walkSpeed;
  if (dragging) speed = r.stance === 'crawl' ? DRAG.crawlSpeed : DRAG.speed;
  if (downed) speed = RANGER.downedSpeed;
  // Hauling a body is already slow, and is done walking backwards as often as not.
  else if (r.stance === 'stand' && !r.running && !dragging) {
    if (r.backpedal) speed *= RANGER.backpedalSpeed;
    else if (offAim > 60 * DEG) speed *= RANGER.strafeSpeed;
  }
  if (r.running) speed *= 1 - over * RANGER.weightRunPenalty;
  speed *= 1 - r.wounds * RANGER.woundSpeedPenalty;
  if (r.staggerTicks > 0) speed *= RANGER.staggerSpeed;
  // HAZ: wading and ice are slow going (02 §4); splashing is louder than walking.
  const wading = waterAt(statics.def, r.x, r.z);
  const onIce = !wading && iceAt(statics.def, r.x, r.z);
  if (wading) speed *= HAZARDS.wadeSpeed;
  else if (onIce) speed *= HAZARDS.iceSpeed;

  // Timed actions need you to hold still: moving cancels them (boarding the ladder excepted).
  if (wantsMove && r.action !== 'none' && r.action !== 'board') {
    r.action = 'none';
    r.actionTicks = 0;
  }

  // HAZ: on ice his direction of travel lags the stick, and he slides on a little when it is let go (traction.ts).
  const go = tractionDirection(ctx, wantsMove ? mx : 0, wantsMove ? mz : 0, onIce);
  const drift = Math.sqrt(go.x * go.x + go.z * go.z);
  if (drift > 0) {
    const moved = resolveMovement(statics, r.x + go.x * speed * DT, r.z + go.z * speed * DT, RANGER.radius);
    const dx = moved.x - r.x;
    const dz = moved.z - r.z;
    r.travelled += Math.sqrt(dx * dx + dz * dz);
    r.x = moved.x;
    r.z = moved.z;
  }
  r.moving = wantsMove || drift > 0.1;

  // Prone or down in a trench he is out of the wind and off his feet: the deepest breath he can take.
  const sheltered = r.stance === 'crawl' || r.inTrench;
  if (r.running) {
    let rate = 1 / (RANGER.fatigueFillSeconds * mods.staminaPool);
    rate *= 1 + over * RANGER.weightFatiguePenalty + r.wounds * RANGER.woundFatiguePenalty;
    if (statics.def.biome === 'desert') rate *= 1 + RANGER.desertFatiguePenalty;
    // R5: the Storm modifier's "stamina drain +20 %", which nothing read (ROUND5 §3.2). It rides here,
    // beside the desert's heat, because a matching stamina POOL is how Cold Blooded and the rest cancel
    // it: the pool divides this same rate, so 1.2 against 1.2 comes back to exactly nothing.
    rate *= stormFatigue(statics.def);
    r.fatigue = Math.min(1, r.fatigue + rate * DT);
    if (r.fatigue >= 1) r.winded = true;
  } else {
    const drain = sheltered ? RANGER.drainProneSeconds : wantsMove ? RANGER.drainWalkSeconds : RANGER.drainStillSeconds;
    // R5: Second Wind says "while prone or in a trench" and was paying out standing in the open
    // as well (ROUND5 §3.5). Upright, everyone recovers at the same rate.
    const recovery = sheltered ? mods.staminaRecovery : 1;
    // Hauling a body is work: no recovery while dragging.
    if (!dragging) r.fatigue = Math.max(0, r.fatigue - (DT / drain) * recovery);
  }
  // HAZ: pushing through water is work on top of all that (02 §4); Swimmer's `wadeFatigue` 0 makes it free.
  if (wading && wantsMove) {
    r.fatigue = Math.min(1, r.fatigue + HAZARDS.wadeFatiguePerSecond * mods.wadeFatigue * DT);
    if (r.fatigue >= 1) r.winded = true;
  }

  if (wantsMove) {
    r.footstepTicks++;
    if (r.footstepTicks >= NOISE.footstepEveryTicks) {
      r.footstepTicks = 0;
      const radius = footstepGait(r, mods) * (wading ? HAZARDS.wadeNoise : 1);
      makeNoise(ctx, r.x, r.z, dragging ? Math.max(radius, NOISE.walk) : radius, 'step');
    }
  }
}
